// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
import computeMsiEffectValue from './computeMsiEffectValue'
import msiValueExclusion from './msiValueExclusion'
import { scoresForEffect } from '../meetstandaard/buildScoresFromStandaardDocument'

/**
 * @typedef {object} MsiEffectData
 * @property {Array<object>} records - Response records for this effect's indicator field(s)
 * @property {Array<{ Title: string, posNeg?: string }>} rowDefs - Raw MatrixQuestionRows defs
 * @property {number} lowerScale - Scale lower bound (ReachStart)
 * @property {number} upperScale - Scale upper bound (ReachEnd)
 */

/**
 * @typedef {object} MsiEffectBreakdownEntry
 * @property {string} effectId - OutputEffects.ID
 * @property {string} msiId - meetstandaard-API effect id (OutputEffects.MSIId)
 * @property {string} name - Effect title
 * @property {boolean} excluded - True for the Participatieladder effect (double-counting guard)
 * @property {boolean} noScores - True when the API has no monetary scores for this effect
 * @property {string | null} [standaardVersion] - Version the amounts come from (versioned standaarden only)
 * @property {string | null} [versionProblem] - Why a versioned effect got no scores: 'geen-versie' | 'gemengde-versies' | 'onbekende-versie'
 * @property {Array<object>} [scores] - The monetary levels the effect was valued at
 * @property {number} deltaTotal - € value created (0 when excluded or noScores)
 * @property {Array<object>} perPersona - Per-persona deltas (see computeMsiEffectValue)
 * @property {object | null} anonymous - Anonymous aggregate delta
 * @property {number | null} stateValue - Last-moment situation value
 * @property {number} momentCount - Distinct measured moments
 */

// Zero-value entry shape shared by the excluded/no-data branches
const EMPTY_VALUE = {
    noScores: false, momentCount: 0, deltaTotal: 0, perPersona: [], anonymous: null, stateValue: null,
    attributedTotal: 0, notAttributedCount: 0, notAttributedReasons: {},
}

/**
 * Combines per-effect MSI monetisation results into one tenant-wide breakdown.
 * The Participatieladder effect is listed but hard-excluded from the total —
 * its value lives in the kenmerken-based participation stream. Effects without
 * monetary scores stay in the list flagged `noScores` so the €0 contribution
 * is visible instead of silent.
 *
 * @param {object} params
 * @param {Array<{ ID: string, MSIId?: string, Effect?: string }>} params.effects - The tenant's MSI OutputEffects docs
 * @param {Object<string, MsiEffectData>} params.dataByEffectId - Fetched records/rowDefs/scale per OutputEffects.ID
 * @param {Object<string, Array<{ score: number, monetaryValue: number }>>} params.scoresByMsiId - Monetary scores per MSI id (`id@version` for versioned standaarden)
 * @param {Object<string, object>} [params.personasById] - Personas-docs op ID, voor de tijdshorizon (rekenregels)
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [params.orgDefault] - CompagnyMeta.DefaultInterventionDose
 * @returns {{ total: number, perEffect: Array<MsiEffectBreakdownEntry>, attributedTotal: number, notAttributedCount: number, notAttributedReasons: Object<string, number> }}
 */
const buildMsiValueBreakdown = ({ effects, dataByEffectId, scoresByMsiId, personasById = {}, orgDefault = null } = {}) => {
    const list = Array.isArray(effects) ? effects : []
    const nameById = new Map(list.map((e) => [e?.ID, e?.Effect || '']))

    const perEffect = list.map((effect) => {
        const msiId = effect?.MSIId || ''
        const base = { effectId: effect?.ID || '', msiId, name: effect?.Effect || '' }

        // De participatieladder, of een effect waarvan een ander effect de
        // waarde draagt (samengevoegd in arbeidsparticipatie 1.0).
        const exclusion = msiValueExclusion(effect)
        if (exclusion) {
            return {
                ...base,
                excluded: true,
                excludedReason: exclusion.reason,
                countedByName: exclusion.countedBy ? (nameById.get(exclusion.countedBy) || null) : null,
                ...EMPTY_VALUE,
            }
        }

        const data = (dataByEffectId || {})[effect?.ID] || {}
        const { scores, standaardVersion, versionProblem } = scoresForEffect(scoresByMsiId, msiId, data.rowDefs)
        if (!scores) {
            return { ...base, excluded: false, ...EMPTY_VALUE, noScores: true, standaardVersion, versionProblem }
        }

        const value = computeMsiEffectValue({
            records: data.records || [],
            rowDefs: data.rowDefs || [],
            lowerScale: data.lowerScale ?? 1,
            upperScale: data.upperScale ?? 5,
            scores,
            personasById,
            orgDefault,
        })
        // The scores travel along: the onderbouwing modal shows the levels this
        // effect was valued at, which differ per version.
        return { ...base, excluded: false, ...value, scores, standaardVersion }
    })

    const counted = perEffect.filter((entry) => !entry.excluded)
    const total = counted.reduce((sum, entry) => sum + entry.deltaTotal, 0)
    // Toegerekende waarde (rekenregels MSI): naast het bruto totaal, met de
    // telling van wat niet doorgerekend kon worden — nooit als 0 meegeteld.
    const attributedTotal = counted.reduce((sum, entry) => sum + entry.attributedTotal, 0)
    const notAttributedCount = counted.reduce((sum, entry) => sum + entry.notAttributedCount, 0)
    const notAttributedReasons = {}
    counted.forEach((entry) => {
        Object.entries(entry.notAttributedReasons).forEach(([reason, count]) => {
            notAttributedReasons[reason] = (notAttributedReasons[reason] || 0) + count
        })
    })

    return { total, perEffect, attributedTotal, notAttributedCount, notAttributedReasons }
}

export default buildMsiValueBreakdown
